import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Tooltip,
  useTheme,
} from '@mui/material';
import HarnessLogo from '../../assets/harnessLogo.svg?react';
import {
  ViewKanban as KanbanIcon,
  AdminPanelSettings as OpsIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { useAuth } from "../../contexts/AuthContext";
import { userCanAccessOps } from '../../utils/userPermissions';

const SIDEBAR_WIDTH = 72; // Slender sidebar width

export function SideNavigation({
  fixed=false,
} : {
  fixed?: boolean;
}) {
  const theme = useTheme();
  const location = useLocation();
  const { user } = useAuth();

  const canAccessOpsPages = userCanAccessOps(user);

  // Define main navigation items
  const mainNavigationItems = [
    {
      path: '/jobs',
      icon: HomeIcon,
      label: 'Home',
    },
  ];

  // Define ops navigation items (shown at bottom) for internal staff
  const opsNavigationItems = canAccessOpsPages ? [
    {
      path: '/ops',
      icon: OpsIcon,
      label: 'Ops Admin',
    },
  ] : [];

  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: '100vh',
        backgroundColor: '#FFFFFF',
        borderRight: '1px solid #E7E7E7',
        display: 'flex',
        flexDirection: 'column',
        position: fixed ? 'fixed' : 'static',
        left: 0,
        top: 0,
        zIndex: theme.zIndex.drawer,
      }}
    >
      {/* Logo/Brand area */}
      <Box
        sx={{
          padding: '20px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <HarnessLogo />
      </Box>

      {/* Main Navigation Items */}
      <List sx={{ flex: 1, p: 1 }}>
        {mainNavigationItems.map((item, index) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;

          return (
            <ListItem key={`${item.path}-${index}`} disablePadding sx={{ mb: 0.5 }}>
              <Tooltip title={item.label} placement="right" arrow>
                <ListItemButton
                  component={RouterLink}
                  to={item.path}
                  sx={{
                    minHeight: 56,
                    borderRadius: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    backgroundColor: isActive 
                      ? `${theme.palette.info.main}14`
                      : 'transparent',
                    color: isActive
                      ? theme.palette.info.main
                      : theme.palette.text.secondary,
                    '&:hover': {
                      backgroundColor: isActive
                        ? `${theme.palette.info.main}14`
                        : theme.palette.action.hover,
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      justifyContent: 'center',
                      color: 'inherit',
                    }}
                  >
                    <Icon />
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>

      {/* Ops Navigation Items - Bottom */}
      {opsNavigationItems.length > 0 && (
        <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
          <List>
            {opsNavigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname.startsWith(item.path);

              return (
                <ListItem key={item.path} disablePadding>
                  <Tooltip title={item.label} placement="right" arrow>
                    <ListItemButton
                      component={RouterLink}
                      to={item.path}
                      sx={{
                        minHeight: 56,
                        borderRadius: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        backgroundColor: isActive 
                          ? theme.palette.error.main 
                          : 'transparent',
                        color: isActive 
                          ? 'white' 
                          : theme.palette.text.secondary,
                        '&:hover': {
                          backgroundColor: isActive 
                            ? theme.palette.error.dark 
                            : theme.palette.action.hover,
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          justifyContent: 'center',
                          color: 'inherit',
                        }}
                      >
                        <Icon />
                      </ListItemIcon>
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}
    </Box>
  );
}

export { SIDEBAR_WIDTH };
